/**
 * P3 SP2 — map-side lens consumers. Pure (no React): the node×mode matrix in
 * the test next door IS the spec (rev 1.1 §3/§5).
 *
 * Subject table:
 *   spine_root_marker / peer_response  → {kind:'peer', personaId, trailId}
 *   turn_marker / merge_node / squad_merge → {kind:'operator', trailId}
 *     (operator-kind ≡ spec's "inherit parent spine" for merges in persona
 *     modes — their layout parents are always-on there — and merges land on
 *     the MAIN trail (SP1 T1), so their OWN stamped trail governs in trail
 *     mode regardless of parent kind.)
 *   everything else → 'inherit': resolve via data.turnKey → that turn's
 *     marker visibility (assistant inherits the user's trail — SP1 T2 — so
 *     this IS "inherit the parent turn"), else data.parentMessageId (fan
 *     chips), else one hop through the incoming edge (artifact tiles),
 *     else on. consult_peers `peer` fan members inherit too (plan delta 9 —
 *     main-turn telemetry, not council camps; conscious v1 gap).
 *
 * Standard turns anchor inheritors on operator subjects (lit in persona
 * modes); orphan-asst turns (turnKey 'unknown-<id>') fall through to the
 * edge hop and follow their persona parent — see the matrix test.
 *
 * Edges are NOT subjects — an edge ghosts iff EITHER endpoint is off.
 */
import type { Node, Edge } from 'reactflow';

import {
  lensVisibility,
  type LensSnapshot,
  type LensSubject,
} from '../../../../utils/lensVisibility';

export function nodeLensSubject(node: Node): LensSubject | 'inherit' {
  const d: any = node.data ?? {};
  switch (node.type) {
    case 'spine_root_marker':
    case 'peer_response':
    // SP-3a — the intent-chat panel lenses as its foreman persona (peer
    // subject); a personaId-less panel inherits rather than emit garbage.
    case 'spine_chat_panel':
      if (!d.personaId) return 'inherit';   // never emit a malformed peer subject
      return { kind: 'peer', personaId: d.personaId, trailId: d.trailId ?? null };
    case 'turn_marker':
    case 'merge_node':
    case 'squad_merge':
      return { kind: 'operator', trailId: d.trailId ?? null };
    default:
      return 'inherit';
  }
}

export interface LensGhosting {
  offNodeIds: Set<string>;
  offEdgeIds: Set<string>;
}

const EMPTY: LensGhosting = { offNodeIds: new Set(), offEdgeIds: new Set() };

export function computeLensGhosting(
  nodes: Node[],
  edges: Edge[],
  snap: LensSnapshot | null,
): LensGhosting {
  // Fast path — no lens, or nothing to filter: callers keep array identity.
  if (!snap) return EMPTY;
  if (snap.activePersonaIds.size === 0 && !snap.activeTrailId) return EMPTY;

  // Pass 1 — direct subjects.
  const vis = new Map<string, 'on' | 'off'>();
  const visByMessageId = new Map<string, 'on' | 'off'>();
  const inheritors: Node[] = [];
  for (const n of nodes) {
    const subj = nodeLensSubject(n);
    if (subj === 'inherit') { inheritors.push(n); continue; }
    const v = lensVisibility(snap, subj);
    vis.set(n.id, v);
    const mid = (n.data as any)?.messageId;
    if (mid) visByMessageId.set(mid, v);
  }

  // Pass 2 — inheritors: turnKey → parentMessageId → one-hop edge → on.
  // Standard turns anchor on operator subjects (on in persona modes); only
  // trail mode — or an orphan-asst turn whose edge hop lands on a persona
  // node — can ghost an inheritor.
  const sourceByTarget = new Map<string, string>();
  for (const e of edges) {
    if (!sourceByTarget.has(e.target)) sourceByTarget.set(e.target, e.source);
  }
  for (const n of inheritors) {
    const d: any = n.data ?? {};
    const inherited =
      (d.turnKey && visByMessageId.get(d.turnKey)) ??
      (d.parentMessageId && visByMessageId.get(d.parentMessageId)) ??
      (sourceByTarget.has(n.id) ? vis.get(sourceByTarget.get(n.id)!) : undefined) ??
      'on';
    vis.set(n.id, inherited);
  }

  const offNodeIds = new Set<string>();
  for (const [id, v] of vis) if (v === 'off') offNodeIds.add(id);

  const offEdgeIds = new Set<string>();
  for (const e of edges) {
    if (offNodeIds.has(e.source) || offNodeIds.has(e.target)) offEdgeIds.add(e.id);
  }
  return { offNodeIds, offEdgeIds };
}
